import React, { useState } from 'react';
import { Divider } from '@chakra-ui/react';
import CustomChakraButton from '../../CustomChakraButton/CustomChakraButton';
import CustomHorizontalTabs from '../../CustomHorizontalTabs/CustomHorizontalTabs';
import calculatePrice from '../../../utils/calculators/calculatePrice';
import CheckboxInput from '../../Forms/CheckboxInput/CheckboxInput';
import FormInput from '../../Forms/FormInput/FormInput';
import StickyNotes from '../../StickyNotes/StickyNotes';
import { useSafeTranslation } from '../../../hooks/useSafeTranslation';
import { useParentAssignedAccounts } from './useParentAssignedAccounts';
import { ChildOverview } from '../ChildOverview';
import './ParentAssignedAccounts.css';
import CustomInput from '../../CustomInput/CustomInput';

const ParentAssignedAccounts = () => {
  const { t } = useSafeTranslation();
  const [saldoFocused, setSaldoFocused] = useState(false);

  function isGreater(value1: string, value2: string): boolean {
    const parse = (val: string): number => parseFloat(val.replace(',', '.'));

    const num1 = parse(value1);
    const num2 = parse(value2);

    return num1 > num2;
  }

  const {
    selectedConsumer,
    updateConsumer,
    areButtonActive,
    setAreButtonsActive,
    customers,
    setConsumerId,
    consumerId,
    setConsumerUpdateContext,
    consumerUpdateContext,
    setConsumerContext,
    consumerContext,
    fetchUpdateConsumerContext,
    creditLimitError,
    setCreditLimitError
  } = useParentAssignedAccounts();



  return (
    <section className="ParentAssignedAccounts flex flex-col lg:pr-2.5">
      <div className="flex flex-col lg:px-spacing-xl pb-spacing-3xl gap-spacing-xl">
        <CustomHorizontalTabs
          onChange={(value) => setConsumerId(value)}
          data={
            customers &&
            customers.map((consumer) => ({
              value: consumer.id,
              label: `${consumer.firstName} ${consumer.lastName}`
            }))
          }
        />
        {selectedConsumer && (
          <ChildOverview
            child={selectedConsumer}
            showBalance
            showTenantName
            consumerId={consumerId}
          />
        )}
      </div>
      <div className="flex items-center gap-spacing-md mt-6">
        <Divider className="basis-1/2" />
        <p className="text-nowrap text-sm font-medium text-grayLight-600">
          {t('parent_assigned_accounts.edit_profile')}
        </p>
        <Divider className="basis-1/2" />
      </div>
      <form
        onSubmit={updateConsumer}
        className="flex flex-col mt-6 gap-spacing-6xl pl-spacing-3xl pr-spacing-3xl">
        <div className="flex flex-col lg:flex-row gap-spacing-xl">
          <div className="w-full lg:basis-1/2">
            <FormInput
              label={t('parent_assigned_accounts.identifier')}
              placeholder={t('parent_assigned_accounts.identifier_placeholder')}
              inputParams={{
                className: 'w-full',
                onChange: (e) => {
                  const target = e.target as HTMLInputElement;
                  const value = target.value.replace(/\D/g, '');
                  setAreButtonsActive(true);
                  setConsumerUpdateContext((prev) => {
                    if (!prev) return undefined;

                    return {
                      ...prev,
                      cardNumber: value
                    };
                  });
                },
                pattern: '\\d{10}',
                title: t('parent_assigned_accounts.identifier_alert'),
                maxLength: 10,
                value: consumerUpdateContext?.cardNumber
              }}
              bottomElement={
                <p className="font-normal text-sm text-grayLight-600">
                  {t('parent_assigned_accounts.identifier_hint')}
                </p>
              }
            />
          </div>
          <div className="w-full lg:basis-1/2 justify-end">
            <div className="hidden lg:flex justify-end gap-spacing-3xl">
              <CustomChakraButton
                iconPosition="onlyText"
                hierarchy="tertiaryGray"
                size="md"
                buttonProps={{
                  onClick: () => {
                    fetchUpdateConsumerContext(Number(consumerId));
                    setAreButtonsActive(false);
                  },
                  type: 'button',
                  disabled: !areButtonActive
                }}>
                {t('parent_assigned_accounts.cancel')}
              </CustomChakraButton>
              <CustomChakraButton
                iconPosition="onlyText"
                hierarchy="secondaryColor"
                size="md"
                buttonProps={{
                  type: 'submit',
                  disabled: !areButtonActive
                }}>
                {t('parent_assigned_accounts.save_changes')}
              </CustomChakraButton>
            </div>
          </div>
        </div>
        <div className="w-full flex flex-col lg:flex-row gap-spacing-xl items-baseline">
          <div className="w-full flex flex-col basis-1/2">
            <label className="font-medium text-sm text-grayLight-700">
              {t('parent_assigned_accounts.remarks')}
            </label>
            <div className="flex flex-col gap-spacing-xs">
              <textarea
                cols={15}
                rows={8}
                maxLength={500}
                placeholder={t('parent_assigned_accounts.remarks_placeholder')}
                className={`ParentAssignedAccounts__textArea border border-grayLight-300 pt-spacing-lg pb-spacing-lg rounded-xl resize-none ${
                  !consumerContext?.parentsCanEditChildDesc
                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                    : 'bg-white text-black'
                }`}
                value={consumerUpdateContext?.remarks}
                disabled={!consumerContext?.parentsCanEditChildDesc}
                onChange={(e) => {
                  setAreButtonsActive(true);
                  setConsumerUpdateContext((prev) => {
                    if (!prev) return undefined;

                    return {
                      ...prev,
                      remarks: e.target.value
                    };
                  });
                }}
                style={{ paddingRight: '14px', paddingLeft: '14px' }}
              />
              <div className="flex justify-end">
                <span className="font-normal text-sm text-grayLight-600">
                  {consumerUpdateContext?.remarks?.length}/500
                </span>
              </div>
            </div>
          </div>
          <div className="basis-1/2">
            <label className="font-medium text-sm text-grayLight-700 text-transparent">
              {t('parent_assigned_accounts.remarks')}
            </label>
            <StickyNotes message={t('parent_assigned_accounts.remarks_hint')} />
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-spacing-xl pb-spacing-8xl mb-6">
          <div className="flex flex-col basis-1/2 gap-spacing-xl">
            {consumerContext?.salesPanelActive &&
              <div><label className="font-bold text-sm text-grayLight-700">
                {t('parent_assigned_accounts.allow_credit_purchase_header')}
              </label>
              <CheckboxInput
                checkboxParams={{
                  onChange: () => {
                    setAreButtonsActive(true);
                    setConsumerUpdateContext((prev) => {
                      if (!prev) return undefined;

                      return {
                        ...prev,
                        allowedToBuy: !consumerUpdateContext?.allowedToBuy
                      };
                    });
                  },
                  isChecked: consumerUpdateContext?.allowedToBuy
                }}
                checkboxPosition="items-center"
                customTextSize="16px"
                text={t('parent_assigned_accounts.allow_credit_purchase')}
              />
              </div>
            }
            {consumerUpdateContext?.allowedToBuy && consumerContext?.salesPanelActive && (
              <CustomInput
                inputParams={{
                  step: 0.01,
                  name: 'saldo',
                  value: saldoFocused
                    ? `${consumerUpdateContext?.creditLimitMoneyString}`
                    : consumerUpdateContext?.creditLimitMoneyString !== null
                      ? `${consumerUpdateContext?.creditLimitMoneyString} zł`
                      : '',
                  onFocus: (e) => {
                    setSaldoFocused(true);
                    // opcjonalnie: zaznacz cały tekst po kliknięciu
                    setTimeout(() => e.target.select(), 0);
                  },
                  onBlur: () => {
                    setSaldoFocused(false);
                  },
                  onInput: (e) => {
                    const input = e.target as HTMLInputElement;
                    // POZWALAMY na minus, cyfry, kropki, przecinki
                    input.value = input.value.replace(/[^\d\-,.]/g, '');
                  },
                  onChange: (e) => {
                    const cleaned = e.target.value.replace(/\s*zł/g, '');
                    setCreditLimitError(isGreater(cleaned , calculatePrice(consumerContext?.maximalCreditMoney, null) ));
                    setConsumerUpdateContext((prev) => {
                      if (!prev) return undefined;
                      setAreButtonsActive(true);
                      return {
                        ...prev,
                        creditLimitMoneyString: cleaned
                      };
                    });
                    }
                }}
                type="default"
                size="md"
                destructive={false}
                label= {t('parent_assigned_accounts.maximal_credit_limit', { creditLimit: calculatePrice(consumerContext?.maximalCreditMoney, 'zł') })}
              />
            )}
            {consumerUpdateContext?.allowedToBuy && creditLimitError &&
              <div className="flex justify-end">
                <span className="text-red-600 font-bold">
                  {t('parent_assigned_accounts.maximal_credit_limit_exceeded')}
                </span>
              </div>
            }
          </div>
          {consumerContext?.salesPanelActive &&
            <div className="basis-1/2">
              <StickyNotes message={t('parent_assigned_accounts.credit_limit_hint')} />
            </div>
          }
        </div>
        <div className="flex flex-col lg:hidden px-spacing-xl pb-spacing-9xl gap-spacing-lg">
          <CustomChakraButton
            iconPosition="onlyText"
            hierarchy="primary"
            size="md"
            buttonProps={{
              type: 'submit',
              disabled: !areButtonActive && creditLimitError
            }}>
            {t('parent_assigned_accounts.save_changes')}
          </CustomChakraButton>
          <CustomChakraButton
            iconPosition="onlyText"
            hierarchy="tertiaryColor"
            size="md"
            buttonProps={{
              onClick: () => {
                fetchUpdateConsumerContext(Number(consumerId));
                setAreButtonsActive(false);
              },
              type: 'button',
              disabled: !areButtonActive
            }}>
            {t('parent_assigned_accounts.cancel')}
          </CustomChakraButton>
        </div>
      </form>
    </section>
  );
};

export default ParentAssignedAccounts;
